import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoginForm } from "@/components/auth/login-form";
import { useWorkspaceStore } from "@/store/workspace-store";

const NO_WORKSPACE_FLAG = "no-workspace";

// The orphan check and the flag handling are page-level side effects, but a
// page can mount more than one dialog (the deal profile has one for its own
// gates and one inside the comments box). These module flags coordinate across
// every instance so the check runs once and only one instance shows the
// message. Sign-out always full-reloads the page, which resets the module, so
// they never need clearing for that path.
let sessionGuardRan = false;
let noWorkspaceFlagHandled = false;

/** Current board path, never carrying the auth_error flag. */
function boardPath(): string {
  if (typeof window === "undefined") return "/";
  const params = new URLSearchParams(window.location.search);
  params.delete("auth_error");
  const qs = params.toString();
  return `${window.location.pathname}${qs ? `?${qs}` : ""}`;
}

/**
 * Sign-in dialog for the public raise-board surfaces. Keeps the user on the
 * board instead of sending them to /sign-in and on to the dashboard.
 *
 * Social auth is a full-page redirect; passing the current path as the
 * destination is enough, because Clerk persists it across /sso-callback.
 */
export function BoardAuthDialog({
  open,
  onOpenChange,
  reason,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Why the user is being asked, e.g. "Sign in to upvote this deal". */
  reason?: string;
}) {
  const { isSignedIn } = useUser();
  const { signOut } = useClerk();
  const fetchWorkspaces = useWorkspaceStore((s) => s.fetchWorkspaces);
  const [searchParams, setSearchParams] = useSearchParams();

  // Set when we return from a sign-out prompted by "no workspace". The signal
  // rides in the URL because sign-out reloads the page, which would wipe any
  // React state we set before calling it.
  const [showNoWorkspace, setShowNoWorkspace] = useState(false);

  // Callers pass an inline arrow, so its identity changes every render. Hold it
  // in a ref so the guard effect can call it without depending on it.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  // On arriving back with the flag, one instance shows the message and strips
  // the param so a refresh doesn't resurface it. `noWorkspaceFlagHandled` is set
  // synchronously before any await, so whichever instance's effect runs first
  // wins and later ones bail — safe regardless of mount ordering or timing.
  useEffect(() => {
    if (searchParams.get("auth_error") !== NO_WORKSPACE_FLAG) return;
    if (noWorkspaceFlagHandled) return;
    noWorkspaceFlagHandled = true;
    setShowNoWorkspace(true);
    const next = new URLSearchParams(searchParams);
    next.delete("auth_error");
    setSearchParams(next, { replace: true });
    // Mount-once: read the flag exactly as the board first loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A provider account with no DiligenceGPT workspace lands here signed in but
  // stuck — the workspaces page offers no way out. Detect it and sign back out
  // to the board carrying a flag, so the reload can explain and offer onboarding.
  //
  // Not gated on `open`: email OTP completes with the dialog still open, but
  // social auth redirects away and back, remounting with the dialog closed and
  // the session already live. Gating on `open` would miss the social path,
  // which is where this comes from.
  //
  // No cancellation flag: under StrictMode the effect is invoked, cleaned up,
  // then invoked again on the same instance. A flag set by the phantom cleanup
  // would kill the real run while the ref blocks the re-run, so the check would
  // silently never happen in dev.
  useEffect(() => {
    if (!isSignedIn) {
      // Reset so the next sign-in (e.g. email OTP, which does not reload) is
      // checked afresh.
      sessionGuardRan = false;
      return;
    }
    if (sessionGuardRan) return;
    sessionGuardRan = true;

    void (async () => {
      if (useWorkspaceStore.getState().workspaces.length === 0) {
        try {
          await fetchWorkspaces();
        } catch {
          // A failed request says nothing about whether an account exists, so
          // leave the session alone rather than signing someone out on a blip.
          sessionGuardRan = false;
          return;
        }
      }

      if (useWorkspaceStore.getState().workspaces.length === 0) {
        const params = new URLSearchParams(window.location.search);
        params.set("auth_error", NO_WORKSPACE_FLAG);
        await signOut({
          redirectUrl: `${window.location.pathname}?${params.toString()}`,
        });
      } else if (open) {
        onOpenChangeRef.current(false);
      }
    })();
  }, [isSignedIn, fetchWorkspaces, signOut]);

  const getStartedHref = `/get-started?redirect_url=${encodeURIComponent(
    boardPath()
  )}`;

  return (
    <Dialog
      open={open || showNoWorkspace}
      onOpenChange={(next) => {
        if (!next) setShowNoWorkspace(false);
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[400px]">
        {showNoWorkspace ? (
          <>
            <DialogHeader>
              <DialogTitle>No workspace found</DialogTitle>
              <DialogDescription>
                We couldn't find a DiligenceGPT workspace for this account. Get
                started to set one up, then you'll come straight back here.
              </DialogDescription>
            </DialogHeader>
            <Button asChild className="w-full">
              <a href={getStartedHref}>Get started</a>
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Sign in</DialogTitle>
              <DialogDescription>
                {reason ?? "Sign in to continue on this Raiseboard."}
              </DialogDescription>
            </DialogHeader>
            <LoginForm redirectTo={boardPath()} embedded />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
